import React, { FC, Fragment, useEffect, useState } from 'react';
import {
  SideSheet,
  SideSheetSize,
  SideSheetHeader,
  SideSheetClose,
  Text,
  Box,
  SideSheetFooter,
  Checkbox,
  TextFieldSize,
  Select,
  Spinner,
  SpinnerSize,
} from '@bechtel/gds';
import { EditCaddyProps } from './types';
import { t, Trans } from '@lingui/macro';
import { Formik, FormikProps, Field, FieldArray } from 'formik';
import {
  Caddy,
  CaddyFillerMaterialDiameter,
  CrewMember,
  RodFillerMaterial,
  RodFillerMaterialDetail,
  RodRequestCaddy,
  RodRequestCaddyFillerMaterial,
} from '@bechtel/types';
import { FooterButtonStyled } from '@bechtel/client-common';
import { object, string, array } from 'yup';
import { CaddySelector, FillerMaterialSelector, CrewMemberSelector } from '~/components/molecules';
import { findIndex, map } from 'lodash';
import { StencilSelector } from '@bechtel/client-weld-common';
import { SideSheetBodyStyled, CaddySelectorStyled } from './styled';
import { SubHeaderStyled } from '~/request/styled';
import { useLingui } from '@lingui/react';

export interface EditCaddyFormikState {
  caddy: Caddy | undefined;
  stencils: CrewMember[];
  fillerMaterial: RodRequestCaddyFillerMaterial | undefined;
  pickedUpBy: CrewMember | undefined;
}

const initialValues: EditCaddyFormikState = {
  caddy: undefined,
  fillerMaterial: undefined,
  stencils: [],
  pickedUpBy: undefined,
};

const validationSchema = object().shape({
  fillerMaterial: object()
    .shape({
      fillerMaterial: object().required(t`Filler Material is mandatory`),
      diameters: array()
        .of(
          object().shape({
            heatLotNumber: object().shape({
              id: string().required(t`Heat/Lot Number ID is mandatory`),
              label: string().required(t`Heat/Lot Number Label is mandatory`),
            }),
            diameter: object().shape({
              id: string().required(t`Diameter ID is mandatory`),
              label: string().required(t`Diameter Label is mandatory`),
            }),
          })
        )
        .min(1, t`Select at least one Rod diameter`),
    })
    .required(t`Filler Material is mandatory`),
  pickedUpBy: object().required(t`Select value for Picked by`),
  stencils: array().min(1, t`Select at least 1 welder`),
  caddy: object().shape({
    id: string().required(),
    label: string().required(),
  }),
});

const RodHeatLotNumberItem = ({ item }: any) => {
  const { i18n } = useLingui();
  return (
    <Text size="400" color="grey.1000">
      {item?.label ? item.label : i18n._('Enter Heat/Lot Number')}
    </Text>
  );
};

export const EditCaddy: FC<EditCaddyProps> = ({ caddy, onClose, onUpdateCaddy, welders, loading }) => {
  const [formikInitalValues, setFormikInitalValues] = useState<EditCaddyFormikState>(initialValues);
  const [fillerMaterialData, setFillerMaterialData] = useState<RodFillerMaterial | undefined>();
  const onSelectFillerMaterial = (fillerMaterial: RodFillerMaterial) => {
    setFillerMaterialData(fillerMaterial);
  };
  useEffect(() => {
    if (!caddy) return;
    setFormikInitalValues({
      caddy: caddy.caddy,
      fillerMaterial: caddy.fillerMaterial,
      stencils: caddy.stencils,
      pickedUpBy: caddy.pickedUpBy,
    });
    if (caddy.fillerMaterial?.fillerMaterial?.id) {
      setFillerMaterialData(caddy.fillerMaterial.fillerMaterial);
    }
  }, [caddy]);

  return (
    <SideSheet
      data-testid="edit-caddy-container"
      size={SideSheetSize.DEFAULT}
      visible
      style={{
        boxShadow: `0 1px 5px 0 rgba(0, 0, 0, 0.2), 0 3px 1px -2px rgba(0, 0, 0, 0.12), 0 2px 2px 0 rgba(0, 0, 0, 0.14)`,
      }}
    >
      <SideSheetHeader title={t`Edit Caddy`}>
        <SideSheetClose onClick={() => onClose()} />
      </SideSheetHeader>
      <Formik
        validationSchema={validationSchema}
        initialValues={formikInitalValues}
        enableReinitialize
        onSubmit={(values, helpers) => {
          const { caddy, fillerMaterial, stencils, pickedUpBy } = values;
          const caddyData: RodRequestCaddy = {
            caddy: caddy,
            stencils: stencils?.length ? stencils : [],
            fillerMaterial: fillerMaterial,
            pickedUpBy: pickedUpBy,
          };
          onUpdateCaddy(caddyData);
          helpers.resetForm();
        }}
      >
        {(props: FormikProps<EditCaddyFormikState>) => {
          const { handleSubmit, dirty, isValid, values, setFieldValue } = props;
          return (
            <Fragment>
              <SideSheetBodyStyled>
                <SubHeaderStyled>
                  <Text size="400" weight="600" family="graphik">
                    <Trans>Caddy Details</Trans>
                  </Text>
                </SubHeaderStyled>
                <CaddySelectorStyled data-testid="caddy-number">
                  <Field component={(props) => <CaddySelector fieldName="caddy" {...props} />} />
                </CaddySelectorStyled>
                <Box px={{ sm: '500' }} mt={{ sm: '600' }} data-testid="filler-material">
                  <Field
                    component={(props) => (
                      <FillerMaterialSelector
                        onSelect={onSelectFillerMaterial}
                        fieldName="fillerMaterial.fillerMaterial"
                        {...props}
                      />
                    )}
                  />
                </Box>
                <FieldArray name="fillerMaterial.diameters">
                  {({ push, remove }) => {
                    return (
                      <Box
                        px={{ sm: '500' }}
                        mt={{ sm: '550' }}
                        flexDirection={{ sm: 'column' }}
                        data-testid="rod-details-list"
                      >
                        {map(fillerMaterialData?.rodFillerMaterialDetails, (item: RodFillerMaterialDetail) => {
                          const itemIndex = findIndex(
                            values?.fillerMaterial?.diameters,
                            (diameter: CaddyFillerMaterialDiameter) => {
                              return item.diameter.id === diameter.diameter.id;
                            }
                          );
                          return (
                            <Box
                              alignItems={{ sm: 'center' }}
                              key={item.id}
                              justifyContent={{ sm: 'space-between' }}
                              pb={{ sm: '400' }}
                            >
                              <Box
                                flexBasis={{ sm: '30%' }}
                                mr={{ sm: '400' }}
                                style={{ cursor: 'pointer' }}
                                onClick={() => {
                                  if (itemIndex !== -1) {
                                    remove(itemIndex);
                                  } else {
                                    push({
                                      diameter: { id: item.diameter.id, label: item.diameter.label },
                                      heatLotNumber: { id: '', label: '' },
                                    });
                                  }
                                }}
                              >
                                <Box mr={{ sm: '400' }}>
                                  <Checkbox checked={itemIndex !== -1} readOnly />
                                </Box>
                                <Text family="graphik" size="300" color="bechtel.blue.800">
                                  {item.diameter.label}&nbsp;<Trans>Dia</Trans>
                                </Text>
                              </Box>
                              <Select
                                itemComponent={RodHeatLotNumberItem}
                                selectedComponent={RodHeatLotNumberItem}
                                size={TextFieldSize.SMALL}
                                items={item.rodHeatLotNumber}
                                selectedItem={
                                  values?.fillerMaterial?.diameters?.length
                                    ? values?.fillerMaterial?.diameters[itemIndex]?.heatLotNumber
                                    : t`Enter Heat/Lot Number`
                                }
                                onSelectedItemChange={async (changes) => {
                                  setFieldValue(`fillerMaterial.diameters[${itemIndex}].heatLotNumber`, {
                                    id: changes.selectedItem?.id,
                                    label: changes.selectedItem?.label,
                                  });
                                }}
                                disabled={itemIndex === -1}
                              />
                            </Box>
                          );
                        })}
                      </Box>
                    );
                  }}
                </FieldArray>
                <SubHeaderStyled>
                  <Text size="400" weight="600" family="graphik">
                    <Trans>Welder(s)</Trans>
                  </Text>
                </SubHeaderStyled>
                <Box px={{ sm: '500' }} mt={{ sm: '600' }}>
                  <Field
                    component={(props) => (
                      <StencilSelector
                        {...props}
                        loading={false}
                        placeholder={t`Add Welder`}
                        formLabel={t`Issued to`}
                        fieldName="stencils"
                        items={welders}
                      />
                    )}
                  />
                </Box>
                <Box px={{ sm: '500' }} mt={{ sm: '600' }}>
                  <Field
                    component={(props) => <CrewMemberSelector items={welders} fieldName="pickedUpBy" {...props} />}
                  />
                </Box>
              </SideSheetBodyStyled>
              <SideSheetFooter>
                <Box ml={{ sm: '500' }} justifyContent={{ sm: 'flex-end' }} data-testid="caddy-footer">
                  <Box alignItems={{ sm: 'center' }}>
                    <Box mr={{ sm: '400' }}>
                      <FooterButtonStyled onClick={onClose}>
                        <Trans>Cancel</Trans>
                      </FooterButtonStyled>
                    </Box>
                    <FooterButtonStyled disabled={!dirty || !isValid || loading} onClick={() => handleSubmit()}>
                      <Trans>Update</Trans>&nbsp;
                      {loading && <Spinner size={SpinnerSize.SMALL} />}
                    </FooterButtonStyled>
                  </Box>
                </Box>
              </SideSheetFooter>
            </Fragment>
          );
        }}
      </Formik>
    </SideSheet>
  );
};
